import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import SEO from '../components/SEO';
import { useLanguage } from '../components/LanguageContext';

export default function GalleryPage() {
  const [selectedImage, setSelectedImage] = useState(null);
  const { language } = useLanguage();

  const galleryImages = [
    { 
      url: "/ChatGPT Image 29. apr. 2026, 13.11.09.png", 
      title: language === 'en' ? "Mediterranean atmosphere" : "Middelhavsstemning" 
    },
    { 
      url: "/ChatGPT Image 29. apr. 2026, 13.11.13 (1).png", 
      title: language === 'en' ? "Luxury resort" : "Luksus resort" 
    },
    { 
      url: "https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/378a20d8-af7c-4ed6-9273-84c31896612e.jpg", 
      title: language === 'en' ? "Accessible luxury" : "Tilgængelig luksus" 
    },
    { 
      url: "/ChatGPT Image 29. apr. 2026, 13.11.14 (3).png", 
      title: language === 'en' ? "Beautiful view" : "Smuk udsigt" 
    },
    { 
      url: "/ChatGPT Image 29. apr. 2026, 13.11.14 (4).png", 
      title: language === 'en' ? "Pool area" : "Poolområde" 
    },
  ];

  useEffect(() => {
    if (selectedImage !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [selectedImage]);

  const openLightbox = (index) => {
    setSelectedImage(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = (e) => {
    e.stopPropagation();
    setSelectedImage((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setSelectedImage((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-28 md:pt-32 pb-20 px-4">
      <SEO 
        title={language === 'en' ? "Photo Gallery - Accessible Destinations" : "Billedgalleri - Handicapvenlige Rejsemål"} 
        description={language === 'en' ? "See photos of our accessible hotels, accessible pools, beaches, and beautiful surroundings at our selected destinations." : "Se billeder af vores handicapvenlige hoteller, tilgængelige pools, strande og smukke omgivelser på vores udvalgte rejsemål."}
      />
      {/* Background decoration */}
      <div 
        className="fixed inset-0 pointer-events-none opacity-15 bg-fixed bg-cover bg-center"
        style={{ backgroundImage: 'url("https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/378a20d8-af7c-4ed6-9273-84c31896612e.jpg")' }}
      />

      <div className="max-w-7xl mx-auto relative">
        <header className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-serif text-emerald-600 mb-4">
            {language === 'en' ? "Photo Gallery" : "Billedgalleri"}
          </h1>
          <div className="w-24 h-1 bg-emerald-500 mx-auto rounded-full mb-6"></div>
          <p className="text-slate-600 max-w-2xl mx-auto">
            {language === 'en' 
              ? "A visual overview of Alora Rejser's selected destinations and facilities with a focus on quality and accessibility."
              : "En visuel oversigt over Alora Rejsers udvalgte destinationer og faciliteter med fokus på kvalitet og tilgængelighed."
            }
          </p>
        </header>

        {/* Masonry-style Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {galleryImages.map((img, index) => (
            <div 
              key={index}
              className="group relative cursor-pointer overflow-hidden rounded-2xl shadow-lg aspect-[4/3]"
              onClick={() => openLightbox(index)}
            >
              <img 
                src={img.url} 
                alt={img.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <span className="text-white text-sm font-medium tracking-wider uppercase border border-white/40 px-4 py-2 backdrop-blur-sm">
                  {language === 'en' ? "View image" : "Se billede"}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage !== null && (
        <div 
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 md:p-10 backdrop-blur-sm"
          onClick={closeLightbox}
        >
          <button 
            className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors"
            onClick={closeLightbox}
          >
            <X size={40} />
          </button>

          <button 
            className="absolute left-6 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors p-2 bg-white/5 rounded-full"
            onClick={prevImage}
          >
            <ChevronLeft size={40} />
          </button>

          <button 
            className="absolute right-6 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors p-2 bg-white/5 rounded-full"
            onClick={nextImage}
          >
            <ChevronRight size={40} />
          </button>

          <div className="max-w-5xl w-full h-full flex flex-col items-center justify-center">
            <img 
              src={galleryImages[selectedImage].url} 
              alt={galleryImages[selectedImage].title}
              className="max-w-full max-h-[85vh] object-contain shadow-2xl rounded-sm"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="mt-6 text-white text-center">
              <h3 className="text-2xl font-serif mb-2">{galleryImages[selectedImage].title}</h3>
              <p className="text-white/60">
                {language === 'en' 
                  ? `Image ${selectedImage + 1} of ${galleryImages.length}`
                  : `Billede ${selectedImage + 1} af ${galleryImages.length}`
                }
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
